/* =====================================================================
   AstaLab 2.0 — statisticsEngine.js (round alpha.2, obiettivi 5, 6, 8)

   Porting VERBATIM (o quasi — vedi note puntuali) delle funzioni pure di
   parsing/matching/copertura/dati-mancanti della baseline 1.3.0-beta.6.
   Operano su un "catalogState" con la STESSA forma dello STATE 1.3 per
   la parte statistiche (players[]/statisticsArchives{}/
   externalStatistics[]/currentSeasonKey/statisticsTargetSeasonsOverride/
   ignoredMissingDataEntries[]) — MAI un global STATE: ogni funzione
   riceve `state` esplicitamente. Questo è ciò che permette allo stesso
   modulo di essere usato SIA da ArtifactDemoCatalogRepository (che tiene
   questa forma in memoria/localStorage) SIA — per i soli calcoli di
   sola lettura (copertura, dati mancanti) — da SupabaseCatalogRepository
   dopo aver ricomposto questa stessa forma dalle righe scaricate da
   Postgres: un solo algoritmo, mai due (vedi ARCHITECTURE_2.md, "Dati
   globali vs dati dell'asta" e obiettivo 11 del round).

   Golden test: tests/test_catalog_golden_130.js confronta OGNI funzione
   qui sotto con la stessa identica funzione della baseline 1.3.0-beta.6
   reale (jsdom), sulle fixture reali del bundle.
   ===================================================================== */
import { ROLES } from "../engine/astalabEngine.js";
import { toStr, toInteger, toIntegerOrNull, toNumOrNull, normStr, isPlainObject, nowISO } from "./normalizeUtils.js";

// ---------------------------------------------------------------------
// CSV generico (identico alla 1.3, astalab.html righe 2105-2130)
// ---------------------------------------------------------------------
export function detectDelimiter(text){
  const firstLine = text.split(/\r?\n/)[0]||"";
  const commas = (firstLine.match(/,/g)||[]).length;
  const semis = (firstLine.match(/;/g)||[]).length;
  return semis>commas ? ";" : ",";
}
export function parseCSV(text, delim){
  const rows = [];
  let row = [], field = "", inQuotes=false;
  for(let i=0;i<text.length;i++){
    const c = text[i];
    if(inQuotes){
      if(c === '"'){
        if(text[i+1] === '"'){ field+='"'; i++; } else { inQuotes=false; }
      } else field += c;
    } else {
      if(c === '"') inQuotes = true;
      else if(c === delim){ row.push(field); field=""; }
      else if(c === "\n"){ row.push(field); rows.push(row); row=[]; field=""; }
      else if(c === "\r"){ /* skip */ }
      else field += c;
    }
  }
  if(field.length || row.length){ row.push(field); rows.push(row); }
  return rows.filter(r=> r.some(c=>c!==undefined && c!==""));
}

// ---------------------------------------------------------------------
// normalizeStatisticsRecord (identica alla 1.3, astalab.html righe 1592-1614)
// ---------------------------------------------------------------------
export function normalizeStatisticsRecord(raw){
  const src = isPlainObject(raw) ? raw : {};
  return {
    fantacalcioId: toIntegerOrNull(src.fantacalcioId, 0, 100000000),
    nome: toStr(src.nome, ""),
    squadra: toStr(src.squadra, ""),
    ruolo: ROLES.includes(src.ruolo) ? src.ruolo : "CEN",
    ruoloMantra: toStr(src.ruoloMantra, ""),
    pv: toIntegerOrNull(src.pv, 0, 60),
    mv: toNumOrNull(src.mv, 0, 10),
    fm: toNumOrNull(src.fm, -50, 50),
    gf: toIntegerOrNull(src.gf, 0, 200),
    gs: toIntegerOrNull(src.gs, 0, 300),
    rp: toIntegerOrNull(src.rp, 0, 100),
    rc: toIntegerOrNull(src.rc, 0, 100),
    rigoriSegnati: toIntegerOrNull(src.rigoriSegnati, 0, 100),
    rigoriSbagliati: toIntegerOrNull(src.rigoriSbagliati, 0, 100),
    assist: toIntegerOrNull(src.assist, 0, 200),
    ammonizioni: toIntegerOrNull(src.ammonizioni, 0, 200),
    espulsioni: toIntegerOrNull(src.espulsioni, 0, 50),
    autogol: toIntegerOrNull(src.autogol, 0, 50)
  };
}

// ---------------------------------------------------------------------
// Colonne / righe / stagioni / matching (identici alla 1.3, righe 2540-2665)
// ---------------------------------------------------------------------
export function normalizeHeaderCell(v){
  const s = toStr(v,"").trim().toLowerCase();
  if(s==="r+") return "r+";
  if(s==="r-") return "r-";
  return s.replace(/[.\s]/g,"");
}
export const QUOTAZIONI_HEADER_MAP = {
  "id":"fantacalcioId", "r":"ruolo", "rm":"ruoloMantra", "nome":"nome", "squadra":"squadra",
  "qta":"qtA", "qti":"qtI", "diff":"diffA", "qtam":"qtAM", "qtim":"qtIM", "diffm":"diffM",
  "fvm":"fvm", "fvmm":"fvmM"
};
export const STATISTICHE_HEADER_MAP = {
  "id":"fantacalcioId", "r":"ruolo", "rm":"ruoloMantra", "nome":"nome", "squadra":"squadra",
  "pv":"pv", "mv":"mv", "fm":"fm", "gf":"gf", "gs":"gs", "rp":"rp", "rc":"rc",
  "r+":"rigoriSegnati", "r-":"rigoriSbagliati", "ass":"assist", "amm":"ammonizioni",
  "esp":"espulsioni", "au":"autogol"
};
export const ROLE_LETTER_MAP = {p:"POR", d:"DIF", c:"CEN", a:"ATT"};
export function mapRoleLetterToRole(v){
  const key = toStr(v,"").trim().toLowerCase();
  return ROLE_LETTER_MAP[key] || (ROLES.includes(v)?v:"CEN");
}
export function findHeaderRowIndex(rows, headerMap){
  const expectedKeys = Object.keys(headerMap);
  const threshold = Math.ceil(expectedKeys.length*0.8);
  for(let i=0;i<Math.min(rows.length,8);i++){
    const row = rows[i]||[];
    const normalized = row.map(normalizeHeaderCell);
    const matches = expectedKeys.filter(k=>normalized.includes(k)).length;
    if(matches>=threshold) return i;
  }
  return -1;
}
export function detectSeasonFromText(text){
  const s = toStr(text,"");
  const m = s.match(/(20\d{2})[\s_\/-](\d{2})(?!\d)/);
  if(!m) return null;
  const y1 = m[1], y2 = m[2];
  return { seasonKey: y1+"_"+y2, label: y1+"/"+y2 };
}
export function buildColumnIndexMap(headerRow, headerMap){
  const idxMap = {};
  headerRow.forEach((cell,idx)=>{
    const key = normalizeHeaderCell(cell);
    const field = headerMap[key];
    if(field) idxMap[field] = idx;
  });
  return idxMap;
}
export function cellStr(v){ return v==null ? "" : (typeof v==="string" ? v.trim() : String(v)); }

export function parseQuotazioniRows(sheetRows){
  const headerIdx = findHeaderRowIndex(sheetRows, QUOTAZIONI_HEADER_MAP);
  if(headerIdx<0) return {ok:false, error:"Intestazioni del listone Quotazioni non riconosciute (attese: Id, R, RM, Nome, Squadra, Qt.A, Qt.I, Diff., Qt.A M, Qt.I M, Diff.M, FVM, FVM M)."};
  const idxMap = buildColumnIndexMap(sheetRows[headerIdx], QUOTAZIONI_HEADER_MAP);
  const records = [];
  let errori = 0;
  for(let i=headerIdx+1;i<sheetRows.length;i++){
    const row = sheetRows[i];
    if(!row || row.every(c=>c==null||c==="")) continue;
    const fantacalcioId = toIntegerOrNull(row[idxMap.fantacalcioId], 0, 100000000);
    const nome = cellStr(row[idxMap.nome]);
    if(fantacalcioId==null || !nome){ errori++; continue; }
    records.push({
      fantacalcioId, nome,
      ruolo: mapRoleLetterToRole(row[idxMap.ruolo]),
      ruoloMantra: cellStr(row[idxMap.ruoloMantra]),
      squadra: cellStr(row[idxMap.squadra]),
      qtA: toInteger(row[idxMap.qtA], 0, 0, 999),
      qtI: toInteger(row[idxMap.qtI], 0, 0, 999),
      diffA: toInteger(row[idxMap.diffA], 0, -999, 999),
      qtAM: toInteger(row[idxMap.qtAM], 0, 0, 999),
      qtIM: toInteger(row[idxMap.qtIM], 0, 0, 999),
      diffM: toInteger(row[idxMap.diffM], 0, -999, 999),
      fvm: toInteger(row[idxMap.fvm], 0, 0, 9999),
      fvmM: toInteger(row[idxMap.fvmM], 0, 0, 9999)
    });
  }
  return {ok:true, headerIdx, records, errori};
}
export function parseStatisticheRows(sheetRows){
  const headerIdx = findHeaderRowIndex(sheetRows, STATISTICHE_HEADER_MAP);
  if(headerIdx<0) return {ok:false, error:"Intestazioni delle statistiche non riconosciute (attese: Id, R, Rm, Nome, Squadra, Pv, Mv, Fm, Gf, Gs, Rp, Rc, R+, R-, Ass, Amm, Esp, Au)."};
  const idxMap = buildColumnIndexMap(sheetRows[headerIdx], STATISTICHE_HEADER_MAP);
  const records = [];
  let errori = 0;
  for(let i=headerIdx+1;i<sheetRows.length;i++){
    const row = sheetRows[i];
    if(!row || row.every(c=>c==null||c==="")) continue;
    const fantacalcioId = toIntegerOrNull(row[idxMap.fantacalcioId], 0, 100000000);
    const nome = cellStr(row[idxMap.nome]);
    if(fantacalcioId==null || !nome){ errori++; continue; }
    records.push(normalizeStatisticsRecord({
      fantacalcioId, nome,
      ruolo: mapRoleLetterToRole(row[idxMap.ruolo]),
      ruoloMantra: cellStr(row[idxMap.ruoloMantra]),
      squadra: cellStr(row[idxMap.squadra]),
      pv: row[idxMap.pv], mv: row[idxMap.mv], fm: row[idxMap.fm],
      gf: row[idxMap.gf], gs: row[idxMap.gs], rp: row[idxMap.rp], rc: row[idxMap.rc],
      rigoriSegnati: row[idxMap["rigoriSegnati"]], rigoriSbagliati: row[idxMap["rigoriSbagliati"]],
      assist: row[idxMap.assist], ammonizioni: row[idxMap.ammonizioni],
      espulsioni: row[idxMap.espulsioni], autogol: row[idxMap.autogol]
    }));
  }
  const columnsFound = Object.keys(idxMap);
  return {ok:true, headerIdx, records, errori, columnsFound};
}

// ---------------------------------------------------------------------
// Revisione r5 (audit, blocker 1/2/3/4/5): pipeline STRICT per il percorso di import 2.0
// (listone/statistiche), SEPARATA dai parser legacy sopra (mantenuti INVARIATI — restano usati
// dal golden test contro la baseline 1.3.0-beta.6 reale, mai toccati per non romperlo). Un
// listone/archivio statistiche rappresenta uno SNAPSHOT COMPLETO: "salta la riga invalida e
// continua" (comportamento dei parser legacy sopra, via `errori++/continue`) è pericoloso per il
// percorso 2.0, perché un fantacalcio_id realmente esistente con una riga malformata verrebbe
// silenziosamente trattato come "uscito" (current=false) in un import successivo — MAI accettabile
// per uno snapshot completo. Regola: se UNA SOLA riga dati non vuota è invalida, l'INTERO file
// viene rifiutato PRIMA di qualunque round/clamp/default silenzioso — mai un parziale, mai una
// correzione automatica. Richiede inoltre TUTTI gli header ufficiali (coerente con le fixture
// reali, verificato: nessun test esistente usa un CSV/XLSX con header parziali attraverso
// buildListoneUpdatePreview()/buildStatisticsImportPreview()).
// ---------------------------------------------------------------------
// Revisione r6 (audit, blocker 3): un header ufficiale richiesto che compare PIÙ di una volta nella
// stessa riga (es. "Id;Id;R;...", "Pv;Pv") non deve mai risolversi implicitamente sull'ultima
// colonna (comportamento di buildColumnIndexMap, che sovrascrive idxMap[field] a ogni corrispondenza
// — silenzioso, mai segnalato). Restituisce sia l'indice della riga header candidata SIA, se
// quella riga contiene un header richiesto duplicato, la chiave duplicata — il chiamante deve
// rifiutare il file PRIMA di costruire idxMap in quel caso, mai scegliere una colonna arbitraria.
function findHeaderRowIndexStrict(rows, headerMap, requiredKeys){
  for(let i=0;i<Math.min(rows.length,8);i++){
    const row = rows[i]||[];
    const normalized = row.map(normalizeHeaderCell);
    if(requiredKeys.every(k=>normalized.includes(k))){
      const duplicateKey = requiredKeys.find(k=>normalized.filter(c=>c===k).length>1) || null;
      return { index:i, duplicateKey };
    }
  }
  return { index:-1, duplicateKey:null };
}
function isBlankCellStrict(v){ return v==null || String(v).trim()===""; }
function parseStrictNumber(v){
  const s = String(v).trim().replace(",", ".");
  if(s==="") return { blank:true };
  const n = Number(s);
  if(!isFinite(n)) return { error:true };
  return { value:n };
}
const LISTONE_ROLE_LETTERS = { p:"POR", d:"DIF", c:"CEN", a:"ATT" };
function parseRoleStrict(v){
  const raw = String(v==null?"":v).trim();
  const lower = raw.toLowerCase();
  if(LISTONE_ROLE_LETTERS[lower]) return { value: LISTONE_ROLE_LETTERS[lower] };
  if(ROLES.includes(raw)) return { value: raw };
  return { error:true, raw };
}

const LISTONE_REQUIRED_HEADER_KEYS = Object.keys(QUOTAZIONI_HEADER_MAP);

function validateListoneRowStrict(row, idxMap, rowNumber){
  const label = "Riga " + rowNumber;
  const idRaw = row[idxMap.fantacalcioId];
  if(isBlankCellStrict(idRaw)) return label + ": Id mancante.";
  const idParsed = parseStrictNumber(idRaw);
  if(idParsed.error) return label + ": Id non numerico (\"" + idRaw + "\").";
  if(idParsed.value !== Math.floor(idParsed.value) || idParsed.value <= 0) return label + ": Id deve essere un intero positivo (\"" + idRaw + "\").";

  if(!cellStr(row[idxMap.nome])) return label + ": Nome mancante.";
  if(!cellStr(row[idxMap.squadra])) return label + ": Squadra mancante.";

  const roleParsed = parseRoleStrict(row[idxMap.ruolo]);
  if(roleParsed.error) return label + ": R non valido (\"" + roleParsed.raw + "\") — atteso P/D/C/A o POR/DIF/CEN/ATT, mai un default.";

  const REQUIRED_INTEGER_FIELDS = [["Qt.A","qtA",[0,999]], ["Qt.I","qtI",[0,999]], ["FVM","fvm",[0,9999]]];
  for(const [field, colKey, range] of REQUIRED_INTEGER_FIELDS){
    const raw = row[idxMap[colKey]];
    if(isBlankCellStrict(raw)) return label + ": " + field + " mancante.";
    const parsed = parseStrictNumber(raw);
    if(parsed.error) return label + ": " + field + " non numerico (\"" + raw + "\").";
    if(parsed.value !== Math.floor(parsed.value)) return label + ": " + field + " deve essere un intero, non frazionario (\"" + raw + "\").";
    if(parsed.value < range[0] || parsed.value > range[1]) return label + ": " + field + " fuori range (" + range[0] + ".." + range[1] + "): " + parsed.value + ".";
  }
  return null;
}

/** Pipeline STRICT per il listone (percorso 2.0) — vedi commento del blocco sopra. Richiede TUTTI
 *  gli header ufficiali. Una sola riga invalida rifiuta l'INTERO file: nessun record prodotto,
 *  nessun round/clamp/default silenzioso su Id/Nome/Squadra/R/Qt.A/Qt.I/FVM. */
export function parseQuotazioniRowsStrict(sheetRows){
  const headerResult = findHeaderRowIndexStrict(sheetRows, QUOTAZIONI_HEADER_MAP, LISTONE_REQUIRED_HEADER_KEYS);
  if(headerResult.index<0) return {ok:false, error:"Intestazioni del listone incomplete o non riconosciute (richieste TUTTE: Id, R, RM, Nome, Squadra, Qt.A, Qt.I, Diff., Qt.A M, Qt.I M, Diff.M, FVM, FVM M)."};
  if(headerResult.duplicateKey) return {ok:false, error:"Intestazione duplicata: la colonna \""+(QUOTAZIONI_HEADER_MAP[headerResult.duplicateKey]||headerResult.duplicateKey)+"\" compare più di una volta nell'intestazione del listone."};
  const headerIdx = headerResult.index;
  const idxMap = buildColumnIndexMap(sheetRows[headerIdx], QUOTAZIONI_HEADER_MAP);
  const records = [];
  // Revisione r6 (audit, blocker 1): un fantacalcioId duplicato nello STESSO snapshot è invalido per
  // costruzione — uno snapshot completo non può contenere due righe per lo stesso giocatore (Supabase
  // lo rifiuterebbe comunque tramite il vincolo PK di tmp_valid_ids in import_catalog_snapshot(); la
  // pipeline strict deve rifiutare l'INTERO file con lo stesso esito PRIMA di qualunque scrittura,
  // mai un "first wins"/"last wins"/merge silenzioso, che altrove (Artifact demo) creerebbe due player
  // distinti con lo stesso fantacalcioId e due UUID diversi).
  const seenIds = new Map();
  let rowNumber = headerIdx + 1;
  for(let i=headerIdx+1;i<sheetRows.length;i++){
    rowNumber++;
    const row = sheetRows[i];
    if(!row || row.every(c=>c==null||c==="")) continue;
    const rowError = validateListoneRowStrict(row, idxMap, rowNumber);
    if(rowError) return {ok:false, error:rowError};
    const fantacalcioId = parseStrictNumber(row[idxMap.fantacalcioId]).value;
    if(seenIds.has(fantacalcioId)) return {ok:false, error:"Fantacalcio Id "+fantacalcioId+" duplicato: righe "+seenIds.get(fantacalcioId)+" e "+rowNumber+"."};
    seenIds.set(fantacalcioId, rowNumber);
    records.push({
      fantacalcioId,
      nome: cellStr(row[idxMap.nome]),
      ruolo: parseRoleStrict(row[idxMap.ruolo]).value,
      ruoloMantra: cellStr(row[idxMap.ruoloMantra]),
      squadra: cellStr(row[idxMap.squadra]),
      qtA: parseStrictNumber(row[idxMap.qtA]).value,
      qtI: parseStrictNumber(row[idxMap.qtI]).value,
      // Campi non elencati esplicitamente dall'audit come "obbligatori/strict" — parsing
      // best-effort invariato (legacy toInteger, con clamp) per restare permissivi su colonne
      // descrittive/derivate mai citate come blocker.
      diffA: toInteger(row[idxMap.diffA], 0, -999, 999),
      qtAM: toInteger(row[idxMap.qtAM], 0, 0, 999),
      qtIM: toInteger(row[idxMap.qtIM], 0, 0, 999),
      diffM: toInteger(row[idxMap.diffM], 0, -999, 999),
      fvm: parseStrictNumber(row[idxMap.fvm]).value,
      fvmM: toInteger(row[idxMap.fvmM], 0, 0, 9999)
    });
  }
  if(!records.length) return {ok:false, error:"Il file non contiene righe dati valide."};
  return {ok:true, headerIdx, records, errori:0};
}

const STATISTICHE_REQUIRED_HEADER_KEYS = Object.keys(STATISTICHE_HEADER_MAP);
const STATISTICHE_STRICT_INTEGER_FIELDS = [
  ["Pv","pv",[0,60]], ["Gf","gf",[0,200]], ["Gs","gs",[0,300]], ["Rp","rp",[0,100]], ["Rc","rc",[0,100]],
  ["R+","rigoriSegnati",[0,100]], ["R-","rigoriSbagliati",[0,100]], ["Ass","assist",[0,200]],
  ["Amm","ammonizioni",[0,200]], ["Esp","espulsioni",[0,50]], ["Au","autogol",[0,50]]
];
const STATISTICHE_STRICT_DECIMAL_FIELDS = [["Mv","mv",[0,10]], ["Fm","fm",[-50,50]]];

function validateStatisticheRowStrict(row, idxMap, rowNumber){
  const label = "Riga " + rowNumber;
  const idRaw = row[idxMap.fantacalcioId];
  if(isBlankCellStrict(idRaw)) return label + ": Id mancante.";
  const idParsed = parseStrictNumber(idRaw);
  if(idParsed.error) return label + ": Id non numerico (\"" + idRaw + "\").";
  if(idParsed.value !== Math.floor(idParsed.value) || idParsed.value <= 0) return label + ": Id deve essere un intero positivo (\"" + idRaw + "\").";

  if(!cellStr(row[idxMap.nome])) return label + ": Nome mancante.";

  const roleParsed = parseRoleStrict(row[idxMap.ruolo]);
  if(roleParsed.error) return label + ": R non valido (\"" + roleParsed.raw + "\") — atteso P/D/C/A o POR/DIF/CEN/ATT, mai un default.";

  // Blank consentito (NULL semanticamente ammesso per una statistica non ancora giocata) — 0 resta
  // sempre un valore reale, mai equiparato a mancante (controllato SOLO se il campo è presente).
  for(const [field, colKey, range] of STATISTICHE_STRICT_INTEGER_FIELDS){
    const raw = row[idxMap[colKey]];
    if(isBlankCellStrict(raw)) continue;
    const parsed = parseStrictNumber(raw);
    if(parsed.error) return label + ": " + field + " non numerico (\"" + raw + "\").";
    if(parsed.value !== Math.floor(parsed.value)) return label + ": " + field + " deve essere un intero, non frazionario (\"" + raw + "\").";
    if(parsed.value < range[0] || parsed.value > range[1]) return label + ": " + field + " fuori range (" + range[0] + ".." + range[1] + "): " + parsed.value + ".";
  }
  for(const [field, colKey, range] of STATISTICHE_STRICT_DECIMAL_FIELDS){
    const raw = row[idxMap[colKey]];
    if(isBlankCellStrict(raw)) continue;
    const parsed = parseStrictNumber(raw);
    if(parsed.error) return label + ": " + field + " non numerico (\"" + raw + "\").";
    if(parsed.value < range[0] || parsed.value > range[1]) return label + ": " + field + " fuori range (" + range[0] + ".." + range[1] + "): " + parsed.value + ".";
  }
  return null;
}

function buildStatisticsRecordStrict(row, idxMap){
  const numOrNull = (colKey) => {
    const raw = row[idxMap[colKey]];
    if(isBlankCellStrict(raw)) return null;
    return parseStrictNumber(raw).value;
  };
  return {
    fantacalcioId: parseStrictNumber(row[idxMap.fantacalcioId]).value,
    nome: cellStr(row[idxMap.nome]),
    ruolo: parseRoleStrict(row[idxMap.ruolo]).value,
    ruoloMantra: cellStr(row[idxMap.ruoloMantra]),
    squadra: cellStr(row[idxMap.squadra]),
    pv: numOrNull("pv"), mv: numOrNull("mv"), fm: numOrNull("fm"),
    gf: numOrNull("gf"), gs: numOrNull("gs"), rp: numOrNull("rp"), rc: numOrNull("rc"),
    rigoriSegnati: numOrNull("rigoriSegnati"), rigoriSbagliati: numOrNull("rigoriSbagliati"),
    assist: numOrNull("assist"), ammonizioni: numOrNull("ammonizioni"),
    espulsioni: numOrNull("espulsioni"), autogol: numOrNull("autogol")
  };
}

/** Pipeline STRICT per le statistiche ufficiali (percorso 2.0) — vedi commento del blocco sopra.
 *  Richiede TUTTI gli header ufficiali. Una sola riga invalida rifiuta l'INTERO file — mai un
 *  round/clamp/default silenzioso, `0` resta sempre un valore reale, un campo vuoto resta NULL. */
export function parseStatisticheRowsStrict(sheetRows){
  const headerResult = findHeaderRowIndexStrict(sheetRows, STATISTICHE_HEADER_MAP, STATISTICHE_REQUIRED_HEADER_KEYS);
  if(headerResult.index<0) return {ok:false, error:"Intestazioni delle statistiche incomplete o non riconosciute (richieste TUTTE: Id, R, Rm, Nome, Squadra, Pv, Mv, Fm, Gf, Gs, Rp, Rc, R+, R-, Ass, Amm, Esp, Au)."};
  if(headerResult.duplicateKey) return {ok:false, error:"Intestazione duplicata: la colonna \""+(STATISTICHE_HEADER_MAP[headerResult.duplicateKey]||headerResult.duplicateKey)+"\" compare più di una volta nell'intestazione delle statistiche."};
  const headerIdx = headerResult.index;
  const idxMap = buildColumnIndexMap(sheetRows[headerIdx], STATISTICHE_HEADER_MAP);
  const records = [];
  // Revisione r6 (audit, blocker 2): stesso principio del listone (blocker 1) — un fantacalcioId
  // duplicato nello STESSO archivio statistiche ufficiale di una singola stagione è invalido.
  // Supabase lo rifiuterebbe comunque (tmp_valid_ids in import_statistics_archive()); l'Artifact
  // demo, senza questo controllo, inserirebbe la prima riga e tratterebbe la seconda come un
  // aggiornamento dello stesso giocatore (potendo persino generare un conflitto artificiale) — la
  // pipeline strict deve invece rifiutare l'INTERO file, mai un "1 inserted + 1 conflict".
  const seenIds = new Map();
  let rowNumber = headerIdx + 1;
  for(let i=headerIdx+1;i<sheetRows.length;i++){
    rowNumber++;
    const row = sheetRows[i];
    if(!row || row.every(c=>c==null||c==="")) continue;
    const rowError = validateStatisticheRowStrict(row, idxMap, rowNumber);
    if(rowError) return {ok:false, error:rowError};
    const fantacalcioId = parseStrictNumber(row[idxMap.fantacalcioId]).value;
    if(seenIds.has(fantacalcioId)) return {ok:false, error:"Fantacalcio Id "+fantacalcioId+" duplicato: righe "+seenIds.get(fantacalcioId)+" e "+rowNumber+"."};
    seenIds.set(fantacalcioId, rowNumber);
    records.push(buildStatisticsRecordStrict(row, idxMap));
  }
  if(!records.length) return {ok:false, error:"Il file non contiene righe dati valide."};
  const columnsFound = Object.keys(idxMap);
  return {ok:true, headerIdx, records, errori:0, columnsFound};
}

// ---------------------------------------------------------------------
// Revisione r7 (audit, gap di parità repository Artifact/Supabase): parseQuotazioniRowsStrict/
// parseStatisticheRowsStrict (r5/r6) validano un CSV/XLSX grezzo — ma ArtifactDemoCatalogRepository.
// applyListoneSnapshot()/applyStatisticsImport() si fidavano ciecamente di QUALUNQUE records[]/preview
// già "normalizzato" ricevuto dal chiamante, chiunque esso fosse: un chiamante che costruisce
// records[] a mano (o bypassa in qualunque modo la preview UI) otteneva un esito permissivo — Id
// duplicati accettati (due player con lo stesso fantacalcioId e due UUID diversi), stagione assente/
// non canonica accettata, ruolo/squadra/qtA/qtI/fvm mancanti accettati con default impliciti del
// motore, Pv fuori range scritto realmente — un esito diverso da quello che lo stesso identico input
// avrebbe ottenuto attraverso la preview UI o la RPC Supabase. Queste due funzioni PURE applicano
// ESATTAMENTE lo stesso contratto STRICT a dei record GIA' normalizzati (non a un file grezzo): sono
// la frontiera comune usata da ArtifactDemoCatalogRepository E (per parità/fail-fast, anche se la RPC
// resta il backstop autorevole) da SupabaseCatalogRepository, PRIMA di qualunque mutazione/chiamata
// RPC — mai una backdoor alla frontiera del repository, indipendentemente da come i record sono stati
// costruiti. Riusano le STESSE costanti di range di parseStatisticheRowsStrict (STATISTICHE_STRICT_*)
// per impedire un drift futuro fra pipeline CSV strict, repository e RPC SQL.
// ---------------------------------------------------------------------

// Revisione r8 (audit, gap residuo alla frontiera repository): limite di riga condiviso — le RPC
// (0008) rifiutano già payload con più di 20000 righe; questa costante evita che il limite venga
// duplicato/dimenticato/reso incoerente fra i due validator (listone/statistiche) o fatto driftare
// rispetto al valore SQL. NON è un tentativo di replicare il backstop byte-per-byte di
// octet_length(p_rows::text) <= 15 MB (quello resta esclusivamente lato server, autorevole): qui si
// applica SOLO il conteggio righe, l'unico controllo replicabile senza serializzare come Postgres.
const CATALOG_IMPORT_MAX_ROWS = 20000;

/** Contratto STRICT per l'applicazione di uno snapshot listone GIA' normalizzato (records[] come
 *  prodotti da parseQuotazioniRowsStrict, o costruiti a mano da un chiamante/test). Restituisce un
 *  messaggio di errore (string) alla prima violazione, altrimenti null. MAI un side effect.
 *  Revisione r8 (audit, blocker 4/5): fileChecksum ora obbligatorio (stringa non vuota) — la RPC
 *  import_catalog_snapshot() lo richiede già; il repository Artifact lo accettava prima come
 *  opzionale, un'altra divergenza di parità silenziosa. records.length limitato a
 *  CATALOG_IMPORT_MAX_ROWS, come la RPC. */
export function validateListoneApplyContract(records, seasonInfo, fileChecksum){
  if(!Array.isArray(records) || records.length===0) return "Nessuna riga da applicare: records deve essere un array non vuoto.";
  if(records.length>CATALOG_IMPORT_MAX_ROWS) return "Troppe righe ("+records.length+"), limite "+CATALOG_IMPORT_MAX_ROWS+" per singolo import.";
  if(typeof fileChecksum!=="string" || fileChecksum.trim()==="") return "Checksum del file mancante.";
  if(!seasonInfo || !seasonInfo.seasonKey) return "Stagione mancante: seasonInfo/seasonKey è obbligatorio per applicare un listone.";
  if(!/^\d{4}_\d{2}$/.test(seasonInfo.seasonKey)) return "Stagione non canonica (attesa AAAA_AA): \""+seasonInfo.seasonKey+"\".";
  const seenIds = new Map();
  for(let i=0;i<records.length;i++){
    const rec = records[i];
    const label = "Riga "+(i+1);
    if(rec==null || typeof rec!=="object") return label+": record non valido.";
    if(!Number.isInteger(rec.fantacalcioId) || rec.fantacalcioId<=0) return label+": fantacalcioId mancante o non un intero positivo.";
    if(seenIds.has(rec.fantacalcioId)) return "Fantacalcio Id "+rec.fantacalcioId+" duplicato: righe "+seenIds.get(rec.fantacalcioId)+" e "+(i+1)+".";
    seenIds.set(rec.fantacalcioId, i+1);
    if(!cellStr(rec.nome)) return label+": Nome mancante.";
    if(!cellStr(rec.squadra)) return label+": Squadra mancante.";
    if(!ROLES.includes(rec.ruolo)) return label+": R mancante o non valido (\""+rec.ruolo+"\") — atteso POR/DIF/CEN/ATT.";
    if(!Number.isInteger(rec.qtA) || rec.qtA<0 || rec.qtA>999) return label+": Qt.A mancante, non intera o fuori range (0..999).";
    if(!Number.isInteger(rec.qtI) || rec.qtI<0 || rec.qtI>999) return label+": Qt.I mancante, non intera o fuori range (0..999).";
    if(!Number.isInteger(rec.fvm) || rec.fvm<0 || rec.fvm>9999) return label+": FVM mancante, non intero o fuori range (0..9999).";
  }
  return null;
}

/** Contratto STRICT per l'applicazione di un archivio statistiche GIA' normalizzato (preview come
 *  prodotto da buildStatisticsImportPreview, o costruito a mano da un chiamante/test). Restituisce un
 *  messaggio di errore (string) alla prima violazione, altrimenti null. MAI un side effect.
 *  Revisione r8 (audit, blocker 1/2/3/5): preview.ok deve essere ESATTAMENTE true (non genericamente
 *  truthy — 0/null/undefined/"ok"-string non devono mai passare); preview.fileChecksum ora
 *  obbligatorio (stringa non vuota, come la RPC import_statistics_archive() richiede già); un ruolo
 *  presente ma vuoto ("") viene ora rifiutato esplicitamente (mai equiparato silenziosamente ad
 *  assente/null — la RPC lo rifiuta perché "" non è in ('POR','DIF','CEN','ATT'), la differenza
 *  semantica va chiusa qui); records.length limitato a CATALOG_IMPORT_MAX_ROWS. */
export function validateStatisticsApplyContract(preview){
  if(!preview || typeof preview!=="object") return "Preview non valida.";
  if(preview.ok!==true) return "Preview non valida.";
  if(typeof preview.fileChecksum!=="string" || preview.fileChecksum.trim()==="") return "Checksum del file mancante.";
  if(!preview.season || !preview.season.seasonKey) return "Stagione mancante: preview.season/seasonKey è obbligatorio.";
  if(!/^\d{4}_\d{2}$/.test(preview.season.seasonKey)) return "Stagione non canonica (attesa AAAA_AA): \""+preview.season.seasonKey+"\".";
  if(!Array.isArray(preview.records) || preview.records.length===0) return "Nessuna riga da applicare: records deve essere un array non vuoto.";
  if(preview.records.length>CATALOG_IMPORT_MAX_ROWS) return "Troppe righe ("+preview.records.length+"), limite "+CATALOG_IMPORT_MAX_ROWS+" per singolo import.";
  const seenIds = new Map();
  for(let i=0;i<preview.records.length;i++){
    const rec = preview.records[i];
    const label = "Riga "+(i+1);
    if(rec==null || typeof rec!=="object") return label+": record non valido.";
    if(!Number.isInteger(rec.fantacalcioId) || rec.fantacalcioId<=0) return label+": fantacalcioId mancante o non un intero positivo.";
    if(seenIds.has(rec.fantacalcioId)) return "Fantacalcio Id "+rec.fantacalcioId+" duplicato: righe "+seenIds.get(rec.fantacalcioId)+" e "+(i+1)+".";
    seenIds.set(rec.fantacalcioId, i+1);
    if(!cellStr(rec.nome)) return label+": Nome mancante.";
    // Il ruolo resta opzionale a questa frontiera (a differenza del listone): alcuni chiamanti
    // storici (es. fixture sintetiche dei test repository) costruiscono record statistici senza
    // ripetere il ruolo, già noto dal listone per lo stesso fantacalcioId — MA se presente non deve
    // MAI essere una stringa vuota: "" è un valore esplicitamente scorretto, mai equivalente ad
    // "assente" (null/undefined restano gli UNICI modi legittimi di omettere il ruolo).
    if(rec.ruolo===""){ return label+": R vuoto non è consentito — usa null/assente per omettere il ruolo, oppure un valore valido (POR/DIF/CEN/ATT)."; }
    if(rec.ruolo!=null && !ROLES.includes(rec.ruolo)) return label+": R non valido (\""+rec.ruolo+"\") — atteso POR/DIF/CEN/ATT.";
    for(const [field, key, range] of STATISTICHE_STRICT_INTEGER_FIELDS){
      const v = rec[key];
      if(v==null) continue; // NULL/assente resta sempre un dato genuinamente mancante, mai un errore
      if(!Number.isInteger(v) || v<range[0] || v>range[1]) return label+": "+field+" non valido (\""+v+"\") — atteso intero "+range[0]+".."+range[1]+".";
    }
    for(const [field, key, range] of STATISTICHE_STRICT_DECIMAL_FIELDS){
      const v = rec[key];
      if(v==null) continue;
      if(typeof v!=="number" || !isFinite(v) || v<range[0] || v>range[1]) return label+": "+field+" non valido (\""+v+"\") — atteso "+range[0]+".."+range[1]+".";
    }
  }
  return null;
}

export function buildFantacalcioIdIndex(players){
  const m = new Map();
  players.forEach(p=>{ if(p.fantacalcioId!=null && !m.has(p.fantacalcioId)) m.set(p.fantacalcioId, p); });
  return m;
}
export function matchExistingPlayerForRow(row, players, byFantacalcioId){
  if(row.fantacalcioId!=null && byFantacalcioId && byFantacalcioId.has(row.fantacalcioId)){
    return byFantacalcioId.get(row.fantacalcioId);
  }
  const aliasMatch = players.find(p=>(p.historicalAliases||[]).some(a=>
    normStr(a.nome)===normStr(row.nome) && normStr(a.squadra)===normStr(row.squadra)));
  if(aliasMatch) return aliasMatch;
  const nameMatch = players.find(p=>
    normStr(p.nome)===normStr(row.nome) && normStr(p.squadra)===normStr(row.squadra) && p.ruolo===row.ruolo);
  if(nameMatch) return nameMatch;
  return null;
}

export function findArchiveRecordForAlias(archive, alias, player){
  const nome = normStr(alias.nome);
  if(alias.squadra && alias.squadra.trim()){
    const squadra = normStr(alias.squadra);
    const foundId = Object.keys(archive.players).find(id=>{
      const rec = archive.players[id];
      return normStr(rec.nome)===nome && normStr(rec.squadra)===squadra;
    });
    return foundId!=null ? archive.players[foundId] : null;
  }
  let candidates = Object.keys(archive.players).filter(id=>normStr(archive.players[id].nome)===nome).map(id=>archive.players[id]);
  if(candidates.length>1 && player && player.ruolo){
    const byRole = candidates.filter(rec=>rec.ruolo===player.ruolo);
    if(byRole.length>=1) candidates = byRole;
  }
  return candidates.length===1 ? candidates[0] : null;
}
/** Porting adattato: richiede sempre `state` esplicito (mai un fallback su un global STATE, che
 *  nel 2.0 non esiste — vedi il commento in cima al file). */
export function getPlayerSeasonStats(player, seasonKey, state){
  if(!player || !seasonKey) return null;
  const archive = (state.statisticsArchives||{})[seasonKey];
  if(!archive || !archive.players) return null;
  if(player.fantacalcioId!=null && archive.players[player.fantacalcioId]) return archive.players[player.fantacalcioId];
  const alias = (player.historicalAliases||[]).find(a=>a.season===seasonKey || a.season===archive.label);
  if(alias) return findArchiveRecordForAlias(archive, alias, player);
  return null;
}

export function parseSeasonKey(seasonKey){
  const m = /^(\d{4})_(\d{2})$/.exec(seasonKey||"");
  return m ? {startYear: parseInt(m[1],10)} : null;
}
export function seasonKeyOffset(seasonKey, offset){
  const p = parseSeasonKey(seasonKey);
  if(!p) return null;
  const newStart = p.startYear+offset;
  return newStart+"_"+String((newStart+1)%100).padStart(2,"0");
}
export function getStatisticsTargetSeasons(state){
  if(Array.isArray(state.statisticsTargetSeasonsOverride) && state.statisticsTargetSeasonsOverride.length){
    return state.statisticsTargetSeasonsOverride.slice();
  }
  if(state.currentSeasonKey){
    return [state.currentSeasonKey, seasonKeyOffset(state.currentSeasonKey,-1), seasonKeyOffset(state.currentSeasonKey,-2)].filter(Boolean);
  }
  return Object.keys(state.statisticsArchives||{}).sort().reverse().slice(0,3);
}
export function getHistoricalSeasonKeys(state){
  const targets = getStatisticsTargetSeasons(state);
  if(state.currentSeasonKey) return targets.filter(sk=>sk!==state.currentSeasonKey);
  return targets.slice(0,2);
}

export function computeMultiseasonCoverage(state, seasonKeys){
  const seasons = seasonKeys && seasonKeys.length ? seasonKeys.slice() : getHistoricalSeasonKeys(state);
  const players = state.players||[];
  const perSeasonPresence = {};
  seasons.forEach(sk=>{ perSeasonPresence[sk] = new Set(); });
  const deficiencies = [];
  players.forEach(p=>{
    seasons.forEach(sk=>{
      const stats = getPlayerSeasonStats(p, sk, state);
      if(stats){ perSeasonPresence[sk].add(p.id); }
      else deficiencies.push({playerId:p.id, nome:p.nome, squadra:p.squadra, ruolo:p.ruolo, seasonKey:sk});
    });
  });
  const both = seasons.length>=2 ? players.filter(p=> seasons.every(sk=>perSeasonPresence[sk].has(p.id))).length : null;
  const onlyBySeasons = {};
  seasons.forEach(sk=>{
    onlyBySeasons[sk] = players.filter(p=>
      perSeasonPresence[sk].has(p.id) && seasons.filter(o=>o!==sk).every(o=>!perSeasonPresence[o].has(p.id))
    ).length;
  });
  const none = players.filter(p=> seasons.every(sk=>!perSeasonPresence[sk].has(p.id))).length;
  const seasonStatus = {};
  seasons.forEach(sk=>{ seasonStatus[sk] = state.statisticsArchives && state.statisticsArchives[sk] ? "imported" : "not-imported"; });
  return {
    seasons, seasonStatus, totalPlayers: players.length, both, onlyBySeasons, none,
    deficiencies, deficienciesCount: deficiencies.length
  };
}
export function computeOverallCoverage(state){ return computeMultiseasonCoverage(state, getStatisticsTargetSeasons(state)); }
export function computeHistoricalCoverage(state){ return computeMultiseasonCoverage(state, getHistoricalSeasonKeys(state)); }

export function computeMissingDataRows(state, seasonKeys, options){
  const opts = options||{};
  const coverage = computeMultiseasonCoverage(state, seasonKeys);
  const playersById = new Map((state.players||[]).map(p=>[p.id,p]));
  const externalByKey = new Map();
  (state.externalStatistics||[]).forEach(rec=>{
    if(!rec.active) return;
    externalByKey.set((rec.fantacalcioId==null?"":rec.fantacalcioId)+"|"+rec.season, rec);
  });
  const ignoredKeys = new Set((state.ignoredMissingDataEntries||[]).map(e=>e.playerId+"|"+e.seasonKey));
  const rows = coverage.deficiencies.map(d=>{
    const p = playersById.get(d.playerId);
    const fantacalcioId = p && p.fantacalcioId!=null ? p.fantacalcioId : "";
    const ext = externalByKey.get(fantacalcioId+"|"+d.seasonKey);
    return Object.assign({}, d, {
      fantacalcioId,
      hasExternalData: !!ext,
      externalSource: ext ? ext.source : "",
      ignored: ignoredKeys.has(d.playerId+"|"+d.seasonKey)
    });
  });
  return opts.includeIgnored ? rows : rows.filter(r=>!r.ignored);
}

// ---------------------------------------------------------------------
// Checksum (identici alla 1.3, righe 2871/... hardening beta.2)
// ---------------------------------------------------------------------
export function computeArchiveChecksum(playersMap){
  const keys = Object.keys(playersMap).sort();
  const str = keys.map(k=>k+":"+JSON.stringify(playersMap[k])).join("|");
  let hash = 5381;
  for(let i=0;i<str.length;i++){ hash = ((hash*33) ^ str.charCodeAt(i)) >>> 0; }
  return hash.toString(16);
}
export function computeFileChecksum(bytes){
  let hash = 5381;
  for(let i=0;i<bytes.length;i++){ hash = ((hash*33) ^ bytes[i]) >>> 0; }
  return hash.toString(16)+"-"+bytes.length.toString(16);
}

// ---------------------------------------------------------------------
// Merge di un record statistico in arrivo con l'esistente (identico alla 1.3)
// ---------------------------------------------------------------------
export const STAT_NUMERIC_FIELDS = ["pv","mv","fm","gf","gs","rp","rc","rigoriSegnati","rigoriSbagliati","assist","ammonizioni","espulsioni","autogol"];
export function isMissingStatValue(field, v){
  return STAT_NUMERIC_FIELDS.includes(field) ? v==null : (v===""||v==null);
}
export function mergeStatisticsRecord(existing, incoming, seasonKey, sourceFile, conflictsOut){
  if(!existing) return {record: incoming, isNew:true, hadConflict:false, wasDuplicate:false};
  const merged = Object.assign({}, existing);
  let anyChanged=false, anyConflict=false;
  Object.keys(incoming).forEach(field=>{
    if(field==="fantacalcioId") return;
    const ev = existing[field], iv = incoming[field];
    const evMissing = isMissingStatValue(field, ev);
    const ivMissing = isMissingStatValue(field, iv);
    if(evMissing && ivMissing) return;
    if(evMissing && !ivMissing){ merged[field]=iv; anyChanged=true; return; }
    if(!evMissing && ivMissing) return;
    if(JSON.stringify(ev)===JSON.stringify(iv)) return;
    conflictsOut.push({
      id: "conflict_" + Math.random().toString(36).slice(2,10),
      fantacalcioId: existing.fantacalcioId, nome: existing.nome, seasonKey, field,
      existingValue: ev, incomingValue: iv, sourceFile, detectedAt: nowISO(), status:"open"
    });
    anyConflict = true;
  });
  return {record: merged, isNew:false, hadConflict:anyConflict, wasDuplicate: !anyChanged && !anyConflict};
}

/** Applica un import di statistiche stagionali a un catalogState (mutazione in-place della sola
 *  fetta `statisticsArchives` — adattamento minimo di applyStatisticsArchiveImportToCandidate() 1.3,
 *  stessa logica di merge/idempotenza tramite fileChecksum, mai un `candidate`/STATE globale). */
export function applyStatisticsImport(catalogState, preview){
  const seasonKey = preview.season.seasonKey;
  const existingArchive = catalogState.statisticsArchives[seasonKey] || {
    seasonKey, label: preview.season.label, importedAt:"", sourceFile:"", rowCount:0, checksum:"", importedFiles:[], players:{}
  };
  if(preview.fileChecksum && existingArchive.importedFiles.some(f=>f.checksum===preview.fileChecksum)){
    return {skipCommit:true, ok:true, duplicateFile:true, seasonKey, label: preview.season.label, sourceFile: preview.sourceFile,
      inseriti:0, aggiornati:0, duplicati: preview.records.length, conflitti:0, errori: preview.errori||0};
  }
  const playersMap = Object.assign({}, existingArchive.players);
  const conflicts = [];
  let inseriti=0, aggiornati=0, duplicati=0;
  preview.records.forEach(rec=>{
    const merge = mergeStatisticsRecord(playersMap[rec.fantacalcioId], rec, seasonKey, preview.sourceFile, conflicts);
    playersMap[rec.fantacalcioId] = merge.record;
    if(merge.isNew) inseriti++;
    else if(merge.wasDuplicate) duplicati++;
    else aggiornati++;
  });
  const importedFiles = existingArchive.importedFiles.concat([{
    name: preview.sourceFile, checksum: preview.fileChecksum||"", importedAt: nowISO(),
    rowCount: preview.records.length, columns: preview.columnsFound||[]
  }]);
  catalogState.statisticsArchives = Object.assign({}, catalogState.statisticsArchives, {
    [seasonKey]: {
      seasonKey, label: preview.season.label, importedAt: nowISO(), sourceFile: preview.sourceFile,
      rowCount: Object.keys(playersMap).length, checksum: computeArchiveChecksum(playersMap),
      importedFiles, players: playersMap
    }
  });
  catalogState.statisticsConflicts = (catalogState.statisticsConflicts||[]).concat(conflicts);
  return {inseriti, aggiornati, duplicati, conflitti: conflicts.length, errori: preview.errori||0, seasonKey, label: preview.season.label, sourceFile: preview.sourceFile, duplicateFile:false};
}
